import type {
  RouteMap,
  Station,
  Pose,
  PlaybackRoute,
  MapCanvasParams,
} from '@/types'

export interface BoundingBox {
  minX: number
  maxX: number
  minY: number
  maxY: number
  width: number
  height: number
  centerX: number
  centerY: number
}

// ルート群の全ポイント座標を収集する（中継点キャッシュ / startSlot・endSlot / slots の順で解決）。
function collectRoutePoints(
  map: RouteMap | null,
  routes: PlaybackRoute[],
  currentPose: Pose | null = null
): { x: number; y: number }[] {
  if (!map) return []

  const allPoints: { x: number; y: number }[] = []

  routes.forEach((route) => {
    if (route.computedRoutePoints) {
      allPoints.push(...route.computedRoutePoints)
      return
    }
    if (route.startSlot && route.endSlot) {
      const routePoints = buildRoutePoints(map, route.startSlot, route.endSlot)
      if (routePoints) {
        routePoints.forEach((station) => {
          if (station.pose) {
            allPoints.push({ x: station.pose.x, y: station.pose.y })
          }
        })
        return
      }
    }
    route.slots.forEach((slotId) => {
      const slot = map.slots.get(slotId)
      if (!slot) return
      const station = map.stations.get(slot.stationId)
      if (!station || !station.pose) return
      allPoints.push({ x: station.pose.x, y: station.pose.y })
    })
  })

  if (currentPose) {
    allPoints.push({ x: currentPose.x, y: currentPose.y })
  }

  return allPoints
}

// ポイント群のバウンディングボックスを計算する。
function calculateBoundingBox(
  points: { x: number; y: number }[]
): BoundingBox | null {
  if (points.length === 0) return null

  const initial = {
    minX: points[0].x,
    maxX: points[0].x,
    minY: points[0].y,
    maxY: points[0].y,
  }

  const bbox = points.reduce(
    (acc, p) => ({
      minX: Math.min(acc.minX, p.x),
      maxX: Math.max(acc.maxX, p.x),
      minY: Math.min(acc.minY, p.y),
      maxY: Math.max(acc.maxY, p.y),
    }),
    initial
  )

  const width = bbox.maxX - bbox.minX
  const height = bbox.maxY - bbox.minY

  return {
    ...bbox,
    width,
    height,
    centerX: (bbox.minX + bbox.maxX) / 2,
    centerY: (bbox.minY + bbox.maxY) / 2,
  }
}

// ルート群がキャンバスに収まる表示幅（メートル）を計算する（autoFit 用）。
export function calculateFitCanvasWidthMeter(
  map: RouteMap | null,
  routes: PlaybackRoute[],
  currentPose: Pose | null,
  canvasParams: MapCanvasParams,
  canvasSize: { width: number; height: number }
): number {
  if (canvasSize.width <= 0 || canvasSize.height <= 0) {
    return canvasParams.canvasWidthMeter || 20
  }

  if (!map || (routes.length === 0 && !currentPose)) {
    return canvasParams.canvasWidthMeter || 20
  }

  const allPoints = collectRoutePoints(map, routes, currentPose)
  if (allPoints.length === 0) {
    return canvasParams.canvasWidthMeter || 20
  }

  const bbox = calculateBoundingBox(allPoints)
  if (!bbox) {
    return canvasParams.canvasWidthMeter || 20
  }

  const { width, height } = bbox

  const minSize = 1
  const effectiveWidth = width < minSize && height < minSize ? minSize : width
  const effectiveHeight = width < minSize && height < minSize ? minSize : height

  const maxDimension = Math.max(effectiveWidth, effectiveHeight)
  const marginRatio = maxDimension < 20 ? 0.15 : 0.1
  const marginPerSide = Math.max(0.3, maxDimension * marginRatio)
  const widthWithMargin = effectiveWidth + marginPerSide * 2
  const heightWithMargin = effectiveHeight + marginPerSide * 2

  const canvasAspectRatio = canvasSize.width / canvasSize.height
  const contentAspectRatio = widthWithMargin / heightWithMargin

  let calculatedWidthMeter: number
  if (contentAspectRatio > canvasAspectRatio) {
    calculatedWidthMeter = widthWithMargin
  } else {
    calculatedWidthMeter = heightWithMargin * canvasAspectRatio
  }

  return Math.max(calculatedWidthMeter, 6)
}

// 現在のキャンバスサイズに基づく表示幅（メートル）を計算する。
export function getEffectiveCanvasWidthMeter(
  canvasParams: MapCanvasParams,
  canvasSize: { width: number; height: number },
  autoFit: boolean,
  calculateFitFn: () => number
): number {
  const defaultWidthMeter = canvasParams.canvasWidthMeter || 20

  if (canvasSize.width <= 0 || canvasSize.height <= 0) {
    return defaultWidthMeter
  }

  if (autoFit) {
    const fitValue = calculateFitFn()
    if (!Number.isFinite(fitValue) || fitValue <= 0) {
      return defaultWidthMeter
    }
    return fitValue
  }

  const baseAspectRatio = 800 / 600
  const currentAspectRatio = canvasSize.width / canvasSize.height

  if (currentAspectRatio > baseAspectRatio) {
    return defaultWidthMeter * (currentAspectRatio / baseAspectRatio)
  }
  return defaultWidthMeter
}

// ルート群のバウンディングボックス中心を返す。
export function calculateRouteCenter(
  map: RouteMap | null,
  routes: PlaybackRoute[],
  currentPose: Pose | null
): { x: number; y: number } | null {
  if (!map || (routes.length === 0 && !currentPose)) return null

  const allPoints = collectRoutePoints(map, routes, currentPose)
  if (allPoints.length === 0) return null

  const bbox = calculateBoundingBox(allPoints)
  if (!bbox) return null

  return { x: bbox.centerX, y: bbox.centerY }
}

// ルート群のワールド座標バウンディングボックスを返す。
export function calculateRoutesBounds(
  map: RouteMap | null,
  routes: PlaybackRoute[]
): BoundingBox | null {
  if (!map) return null
  const points = collectRoutePoints(map, routes, null)
  return calculateBoundingBox(points)
}

// 背景画像がカバーするワールド座標バウンディングボックスを返す。
export function calculateImageBounds(
  bgImage: HTMLImageElement | null,
  canvasParams: MapCanvasParams
): BoundingBox | null {
  const ppm = canvasParams.imagePixelPerMeter
  if (!bgImage || !ppm) return null
  const minX = -canvasParams.imageOriginPixX / ppm
  const maxX = (bgImage.width - canvasParams.imageOriginPixX) / ppm
  const minY = (canvasParams.imageOriginPixY - bgImage.height) / ppm
  const maxY = canvasParams.imageOriginPixY / ppm
  return {
    minX,
    maxX,
    minY,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  }
}

// バウンディングボックスの値ベースのキー。オブジェクトの同一性ではなく
// 範囲の値が変わったときだけ変化させたい場合（autoFit のリセット判定など）に使う。
export function boundsKey(bounds: BoundingBox | null): string {
  if (!bounds) return ''
  return [bounds.minX, bounds.maxX, bounds.minY, bounds.maxY]
    .map((v) => v.toFixed(3))
    .join(',')
}

// バウンディングボックスをキャンバスに収める表示幅（メートル）を返す（アスペクト比考慮）。
export function fitWidthMeterForBounds(
  bounds: BoundingBox,
  canvasParams: MapCanvasParams,
  canvasSize: { width: number; height: number },
  marginRatio = 0.1
): number {
  if (canvasSize.width <= 0 || canvasSize.height <= 0) {
    return canvasParams.canvasWidthMeter || 20
  }
  const w = Math.max(bounds.width * (1 + marginRatio * 2), 0.1)
  const h = Math.max(bounds.height * (1 + marginRatio * 2), 0.1)
  const canvasAspect = canvasSize.width / canvasSize.height
  const contentAspect = w / h
  const widthMeter = contentAspect > canvasAspect ? w : h * canvasAspect
  return Math.max(widthMeter, 1)
}

function getPlaybackRoutePoints(
  map: RouteMap,
  startSlot: { id: string; stationId: string },
  startStation: Station,
  endStation: Station
): Station[] | null {
  const mapIdMatch = startSlot.id.match(/^\*r([^/]+)\//)
  if (!mapIdMatch) return null

  const mapId = mapIdMatch[1]

  const stationsInRoute: { id: string; station: Station }[] = []
  for (const [stationId, station] of map.stations.entries()) {
    const match = stationId.match(/^\*r([^/]+)\/(.+)$/)
    if (match && match[1] === mapId) {
      stationsInRoute.push({ id: stationId, station })
    }
  }

  stationsInRoute.sort((a, b) =>
    a.id.localeCompare(b.id, undefined, { numeric: true })
  )

  if (stationsInRoute.length < 2) return null

  const startIndex = stationsInRoute.findIndex((s) => s.id === startStation.id)
  const endIndex = stationsInRoute.findIndex((s) => s.id === endStation.id)

  if (startIndex === -1 || endIndex === -1) return null

  const from = Math.min(startIndex, endIndex)
  const to = Math.max(startIndex, endIndex)

  const points = stationsInRoute.slice(from, to + 1).map((s) => s.station)
  // 逆方向指定（方向を逆にする / isSwapped）の場合は走行順に合わせて反転する。
  // ステーション列には両方向のステーション（rec-i-j / rec-j-i）が含まれるため、
  // 反転するとアニメーションの theta も逆方向走行時の向きが使われる。
  if (startIndex > endIndex) points.reverse()
  return points
}

// getMap のマップから rec-1/rec-2 スロットを持つ記録ルートを抽出する。
export function extractRoutes(map: RouteMap): PlaybackRoute[] {
  const mapIdToSlots: Record<string, Record<string, string>> = {}

  for (const slot of map.slots.values()) {
    const match = slot.id.match(/^\*r([^/]+)\/(rec-1|rec-2)$/)
    if (!match) continue
    const [, mapId, slotName] = match
    if (!mapIdToSlots[mapId]) mapIdToSlots[mapId] = {}
    mapIdToSlots[mapId][slotName] = slot.id
  }

  return Object.entries(mapIdToSlots)
    .filter(([, slots]) => slots['rec-1'] && slots['rec-2'])
    .map(([mapId, slots]) => {
      const startSlotId = slots['rec-1']
      const endSlotId = slots['rec-2']
      const routePoints = buildRoutePoints(map, startSlotId, endSlotId)
      const computedRoutePoints = routePoints
        ? routePoints
            .filter((s) => s.pose)
            .map((s) => ({ x: s.pose!.x, y: s.pose!.y }))
        : null
      return {
        id: mapId,
        mapId,
        slots: [startSlotId, endSlotId],
        computedRoutePoints,
      }
    })
    .sort((a, b) => b.mapId.localeCompare(a.mapId))
}

export function buildRoutePoints(
  map: RouteMap,
  startSlotId: string,
  endSlotId: string
): Station[] | null {
  const startSlot = map.slots.get(startSlotId)
  const endSlot = map.slots.get(endSlotId)

  if (!startSlot || !endSlot) return null

  const startStation = map.stations.get(startSlot.stationId)
  const endStation = map.stations.get(endSlot.stationId)

  if (!startStation?.pose || !endStation?.pose) return null

  if (startSlotId === endSlotId) {
    return [startStation]
  }

  const mapIdMatch = startSlot.id.match(/^\*r([^/]+)\//)
  if (mapIdMatch) {
    return getPlaybackRoutePoints(map, startSlot, startStation, endStation)
  } else {
    return [startStation, endStation]
  }
}
